import { Box, VStack } from '@src/components/Core';
import { DrawerContent } from '@src/components/Drawer';
import type { EntityAdminClass } from '@src/components/EntityAdmin';
import type { EntityAdminRecord } from '@src/components/EntityAdmin/types';
import type { useEntityAdmin } from '@src/components/EntityAdmin/useEntityAdmin';
import { useCallback, type ReactNode } from 'react';
import { useAppwideDrawerContext } from '../../../../providers';
import { useEntityAdminFormState } from '../../hooks/useEntityAdminFormState';
import { createSaveContext } from '../../utils';
import { EntityAdminDrawerFooter, getEntityAdminDrawerRootFormProps } from '../EntityAdminDrawerFooter';
import { DrawerOptionsList } from './components/DrawerOptionsList';
import { registerDefaultDrawerContentRenderers } from './components/registerDrawerContent';
import type { InputsAndDropdownsDrawerOptions } from './types';

// Register default drawer content renderers
registerDefaultDrawerContentRenderers();

export interface InputsAndDropdownsDrawerProps<
  TRecord extends EntityAdminRecord,
  TDrawerRecord extends EntityAdminRecord,
> extends Omit<BaseEntityAdminDrawerProps<TRecord, TDrawerRecord>, 'onDeleteEntities'> {
  isEditing: boolean;
  addingChildEntity: boolean;
  /** Default values function to use when creating a new entity. */
  newEntityDefault?: () => Partial<TDrawerRecord>;
}

export interface BaseEntityAdminDrawerProps<
  TRecord extends EntityAdminRecord,
  TDrawerRecord extends EntityAdminRecord,
> extends Pick<
  ReturnType<typeof useEntityAdmin<TRecord, TDrawerRecord>>,
  | 'allowDeleteEntity'
  | 'selectedEntities'
  | 'getEntityAsDrawerEntity'
  | 'setIsEntityAdminDrawerDirty'
  | 'suppressEditingFooterActions'
  | 'suppressDrawerFormSubmit'
> {
  onSaveEntities: (
    entityClasses: EntityAdminClass<TRecord>[] | undefined, // undefined if adding a new entity
    updatedEntity: TDrawerRecord,
    context: {
      isCreate: boolean;
      parentEntity?: TRecord; // for child entity creation
    }
  ) => Promise<void>;
  runValidation?: () => void;
  drawerOptions: InputsAndDropdownsDrawerOptions<TDrawerRecord>;
  /** Optional content rendered below the drawer form fields. */
  drawerContentSuffix?: ReactNode;
}
export function InputsAndDropdownsDrawer<TRecord extends EntityAdminRecord, TDrawerRecord extends EntityAdminRecord>({
  selectedEntities,
  onSaveEntities,
  drawerOptions,
  isEditing,
  addingChildEntity,
  getEntityAsDrawerEntity,
  setIsEntityAdminDrawerDirty,
  newEntityDefault,
  suppressEditingFooterActions,
  suppressDrawerFormSubmit,
  drawerContentSuffix,
}: InputsAndDropdownsDrawerProps<TRecord, TDrawerRecord>) {
  const { isGuideVisible } = useAppwideDrawerContext();

  const {
    form,
    handleOnFormUpdate,
    inheritedValues,
    someError,
    prepareFormForSave,
    errorState,
    touchState,
    touchAll,
    originalEntity,
    markFormPristine,
  } = useEntityAdminFormState<TRecord, TDrawerRecord>({
    selectedEntities,
    drawerOptions,
    getEntityAsDrawerEntity,
    setIsEntityAdminDrawerDirty,
    newEntityDefault,
    isEditing,
    addingChildEntity,
  });

  const handleOnSave = useCallback(async () => {
    const formWithInputTypesConverted = prepareFormForSave();
    // Pass EntityAdminClass[] directly (undefined if adding a new entity)
    const entityClasses = isEditing ? selectedEntities : undefined;

    const context = createSaveContext({ isEditing, addingChildEntity, selectedEntities });

    await onSaveEntities(entityClasses, formWithInputTypesConverted, context);
    markFormPristine();
  }, [prepareFormForSave, onSaveEntities, isEditing, addingChildEntity, selectedEntities, markFormPristine]);

  return (
    <VStack
      {...getEntityAdminDrawerRootFormProps(suppressDrawerFormSubmit)}
      data-testid="entity-admin-page-inputs-and-dropdowns-drawer"
      h="100%"
    >
      <DrawerContent overflow="overlay" w="100%">
        <Box>
          <DrawerOptionsList
            drawerOptions={drawerOptions}
            form={form}
            originalEntity={originalEntity}
            inheritedValues={inheritedValues}
            errorState={errorState}
            touchState={touchState}
            onFormUpdate={handleOnFormUpdate}
            isEditing={isEditing}
            isGuideVisible={isGuideVisible}
          />
          {drawerContentSuffix}
        </Box>
      </DrawerContent>
      {!(suppressEditingFooterActions && isEditing) && (
        <EntityAdminDrawerFooter
          handleOnSave={handleOnSave}
          runValidation={touchAll}
          someError={someError}
          suppressDrawerFormSubmit={suppressDrawerFormSubmit}
        />
      )}
    </VStack>
  );
}
